import { apiPost, apiPut } from './api-client';

export interface AssistChange {
  key: string;
  label: string;
  before: string;
  after: string;
  value: unknown;
}

const KIND_BY_URL: Record<string, string> = {
  '/api/customers': 'customer',
  '/api/engagements': 'engagement',
  '/api/charge-lines': 'charge-line',
  '/api/purchase-orders': 'customer-order',
  '/api/invoices': 'invoice',
  '/api/invoice-templates': 'invoice-template',
  '/api/commercial': 'sales-offer',
  '/api/quotations': 'quotation',
  '/api/proposals': 'proposal',
  '/api/payments-in': 'payment-in',
  '/api/vendors': 'vendor',
  '/api/bills': 'bill',
  '/api/expenses': 'expense',
  '/api/people': 'person',
  '/api/payments-out': 'payment-out',
};

const WRITE: Record<string, string> = {
  customer: '/api/customers',
  engagement: '/api/engagements',
  'charge-line': '/api/charge-lines',
  'customer-order': '/api/purchase-orders',
  'supplier-order': '/api/purchase-orders',
  invoice: '/api/invoices',
  'invoice-template': '/api/invoice-templates',
  proposal: '/api/proposals',
  quotation: '/api/quotations',
  'payment-in': '/api/payments-in',
  vendor: '/api/vendors',
  bill: '/api/bills',
  expense: '/api/expenses',
  person: '/api/people',
  'payment-out': '/api/payments-out',
};

const OPEN: Record<string, string> = {
  'customer-order': '/purchase-orders',
  'supplier-order': '/supplier-purchase-orders',
  invoice: '/invoices',
  'invoice-template': '/invoice-templates',
  proposal: '/proposals',
  quotation: '/quotations',
  bill: '/bills',
};

const ROLE: Record<string, string> = {
  customer: 'Finance ops',
  engagement: 'Finance ops',
  'charge-line': 'Finance ops',
  vendor: 'Finance ops',
  person: 'Finance ops',
  invoice: 'AR invoicer',
  'invoice-template': 'AR invoicer',
  'payment-in': 'AR invoicer',
  'customer-order': 'PO creator',
  'supplier-order': 'PO creator',
  proposal: 'Sales offers',
  quotation: 'Sales offers',
  'sales-offer': 'Sales offers',
  bill: 'AP bills',
  expense: 'AP bills',
  'payment-out': 'AP bills',
};

export function assistKind(url: string): string | undefined {
  if (url.includes('direction=supplier')) return 'supplier-order';
  return KIND_BY_URL[url.split('?')[0]];
}

export function assistRole(kind: string): string {
  return ROLE[kind] || 'Finance';
}

export function assistOpen(kind: string, saved: { id?: string; proposalId?: string; quotationId?: string }): string | undefined {
  if (kind === 'sales-offer') {
    if (saved.proposalId) return `/proposals/${saved.proposalId}`;
    if (saved.quotationId) return `/quotations/${saved.quotationId}`;
    return undefined;
  }
  const base = OPEN[kind];
  return base && saved.id ? `${base}/${saved.id}` : undefined;
}

export function assistEditable(kind: string, status: unknown): boolean {
  const value = String(status || '');
  if (kind === 'invoice') return value !== 'issued' && value !== 'paid' && value !== 'credited';
  if (kind === 'proposal' || kind === 'quotation') return value !== 'issued' && value !== 'accepted';
  if (kind === 'supplier-order' || kind === 'customer-order') return value !== 'issued';
  return true;
}

export function onOfferSaved(handler: (ids: { proposalId?: string; quotationId?: string }) => void): () => void {
  const listener = (event: Event) => handler((event as CustomEvent<{ proposalId?: string; quotationId?: string }>).detail || {});
  window.addEventListener('bo-offer-saved', listener);
  return () => window.removeEventListener('bo-offer-saved', listener);
}

export async function applyRecordAssist(kind: string, recordId: string | undefined, changes: AssistChange[], ids?: { proposalId?: string; quotationId?: string }): Promise<Record<string, unknown>> {
  if (kind === 'sales-offer') return applyOffer(changes, ids);
  const url = WRITE[kind];
  if (!url) throw new Error('unknown record');
  const body: Record<string, unknown> = {};
  for (const change of changes) body[change.key] = change.value;
  if (kind === 'supplier-order') body.direction = 'supplier';
  if (kind === 'customer-order') body.direction = 'customer';
  if (recordId) return apiPut<Record<string, unknown>>(`${url}/${recordId}`, body);
  return apiPost<Record<string, unknown>>(url, body);
}

async function applyOffer(changes: AssistChange[], ids?: { proposalId?: string; quotationId?: string }): Promise<Record<string, unknown>> {
  const proposal: Record<string, unknown> = {};
  const quotation: Record<string, unknown> = {};
  for (const change of changes) {
    const [side, key] = change.key.split('.');
    if (side === 'proposal' && key) proposal[key] = change.value;
    if (side === 'quotation' && key) quotation[key] = change.value;
  }
  let proposalId = '';
  if (Object.keys(proposal).length) {
    const existing = ids?.proposalId || '';
    const saved = existing
      ? await apiPut<{ id?: string }>(`/api/proposals/${existing}`, proposal)
      : await apiPost<{ id?: string }>('/api/proposals', proposal);
    proposalId = String(saved.id || existing);
  }
  const linkProposalId = proposalId || ids?.proposalId || '';
  let quotationId = '';
  if (Object.keys(quotation).length) {
    const existing = ids?.quotationId || '';
    const saved = existing
      ? await apiPut<{ id?: string }>(`/api/quotations/${existing}`, { ...quotation, ...(linkProposalId ? { proposalId: linkProposalId } : {}) })
      : await apiPost<{ id?: string }>('/api/quotations', { ...quotation, ...(linkProposalId ? { proposalId: linkProposalId } : {}) });
    quotationId = String(saved.id || existing);
  }
  if (!proposalId && !quotationId) throw new Error('Choose at least one change');
  const saved = { id: proposalId || quotationId, proposalId, quotationId };
  window.dispatchEvent(new CustomEvent('bo-offer-saved', { detail: saved }));
  return saved;
}
